import { ProviderListRow } from '../types';
import { formatHumanName } from '@medplum/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getPractitionerWorkPhoneContact, mapPractitionersToWithRole } from '@bariendo/lib/profile';
import { formatPhoneNumber } from '@bariendo/lib/phone-validations';
import { Practitioner, PractitionerRole } from '@medplum/fhirtypes';
import { usePractitionersByRoles } from '@/hooks/usePractitionersByRoles';

function getPractitionerPhoneNumber(practitioner: Practitioner): string {
  const phone = getPractitionerWorkPhoneContact(practitioner)?.value;
  return phone ? formatPhoneNumber(phone) : '';
}

function buildProviderRows(practitioners: Practitioner[], practitionerRoles: PractitionerRole[]): ProviderListRow[] {
  const practitionersWithRole = mapPractitionersToWithRole(practitioners, practitionerRoles);

  return practitionersWithRole
    .map((practitioner) => ({
      id: practitioner.id ?? '',
      name: formatHumanName(practitioner.name?.[0]) ?? 'Unknown provider',
      role: practitioner.roleLabel ?? '—',
      phoneNumber: getPractitionerPhoneNumber(practitioner),
      practitioner,
    }))
    .filter((row) => row.id)
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

export function useProviderList(searchTerm: string) {
  const { practitioners, practitionerRoles, loading, reload } = usePractitionersByRoles();
  const [providers, setProviders] = useState<ProviderListRow[]>([]);

  useEffect(() => {
    setProviders(buildProviderRows(practitioners, practitionerRoles));
  }, [practitioners, practitionerRoles]);

  const updateProvider = useCallback((updatedPractitioner: Practitioner) => {
    setProviders((current) =>
      current
        .map((row) =>
          row.id === updatedPractitioner.id
            ? {
                ...row,
                practitioner: updatedPractitioner,
                phoneNumber: getPractitionerPhoneNumber(updatedPractitioner),
              }
            : row
        )
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
    );
  }, []);

  const filteredProviders = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return providers;
    }

    return providers.filter((provider) => provider.name.toLowerCase().includes(normalizedSearch));
  }, [providers, searchTerm]);

  return {
    providers: filteredProviders,
    loading,
    reloadProviders: reload,
    updateProvider,
  };
}
